// DatePicker — vlastní kalendář v designu aplikace (nahrazuje nativní <input type="date">).
//
// Pravidla zobrazení:
//   - Dny před `min` jsou zašedlé, nejdou kliknout.
//   - Šipka zpět je vypnutá, když je zobrazený měsíc na/níže než měsíc `min`
//     (nelze se vracet do minulých měsíců, na které stejně nejde kliknout).
//   - Když `min` není zadané (např. management může zpětně), zpět funguje bez omezení.
//
// Použití: <DatePicker value="2026-07-01" min="2026-07-01" onChange={(iso) => ...} />

import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";

const CS_MONTHS = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
const CS_DOW = ["po", "út", "st", "čt", "pá", "so", "ne"];

const pad = (n) => String(n).padStart(2, "0");
const toIso = (y, m0, d) => `${y}-${pad(m0 + 1)}-${pad(d)}`;
function parseIso(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  return m ? { y: +m[1], m0: +m[2] - 1, d: +m[3] } : null;
}
function fmtCz(s) {
  const p = parseIso(s);
  return p ? `${p.d}. ${p.m0 + 1}. ${p.y}` : "";
}
const monthIndex = (y, m0) => y * 12 + m0;

export default function DatePicker({ value, min, onChange, placeholder = "Vyber datum", className = "" }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);

  const sel = parseIso(value);
  const minP = parseIso(min);
  const today = useMemo(() => { const d = new Date(); return { y: d.getFullYear(), m0: d.getMonth(), d: d.getDate() }; }, []);
  const todayIso = toIso(today.y, today.m0, today.d);

  const [view, setView] = useState(() => {
    const b = sel || minP || today;
    return { y: b.y, m0: b.m0 };
  });

  // Když se otevře, srovnej zobrazený měsíc na vybraný/min a spočti pozici popoveru.
  const openPanel = useCallback(() => {
    const b = sel || minP || today;
    setView({ y: b.y, m0: b.m0 });
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      const panelW = 300;
      const left = Math.min(r.left, window.innerWidth - panelW - 8);
      // pod triggerem; když by přeteklo dolů, dej nad něj
      const below = r.bottom + 4;
      const flipUp = below + 340 > window.innerHeight && r.top - 340 > 0;
      setPos({ left: Math.max(8, left), top: flipUp ? r.top - 344 : below });
    }
    setOpen(true);
  }, [sel, minP, today]);

  // ESC zavře
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Měsíční mřížka (pondělí první)
  const weeks = useMemo(() => {
    const first = new Date(view.y, view.m0, 1);
    const startDow = (first.getDay() + 6) % 7; // 0 = pondělí
    const daysInMonth = new Date(view.y, view.m0 + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [view]);

  const canPrev = !minP || monthIndex(view.y, view.m0) > monthIndex(minP.y, minP.m0);
  const goPrev = () => { if (!canPrev) return; setView((v) => v.m0 === 0 ? { y: v.y - 1, m0: 11 } : { y: v.y, m0: v.m0 - 1 }); };
  const goNext = () => setView((v) => v.m0 === 11 ? { y: v.y + 1, m0: 0 } : { y: v.y, m0: v.m0 + 1 });

  const pick = (d) => {
    if (d == null) return;
    const dayIso = toIso(view.y, view.m0, d);
    if (min && dayIso < min) return; // nepovolený den
    onChange?.(dayIso);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => (open ? setOpen(false) : openPanel())}
        className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm border-2 rounded-xl bg-white text-left transition-colors ${open ? "border-brand ring-2 ring-brand-light" : "border-slate-200 hover:border-slate-300"} ${className}`}
      >
        <CalIcon className="w-4 h-4 text-brand shrink-0" />
        <span className={value ? "text-slate-800 font-medium" : "text-slate-400"}>{value ? fmtCz(value) : placeholder}</span>
      </button>

      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} />
          <div className="fixed z-[91] w-[300px] bg-white rounded-2xl shadow-2xl border border-slate-200 p-3 select-none" style={{ left: pos.left, top: pos.top }}>
            {/* hlavička měsíce */}
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="text-sm font-bold text-slate-800 capitalize">{CS_MONTHS[view.m0]} {view.y}</div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={goPrev} disabled={!canPrev}
                  className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${canPrev ? "text-slate-600 hover:bg-slate-100" : "text-slate-200 cursor-not-allowed"}`}
                  title={canPrev ? "Předchozí měsíc" : "Dřívější měsíce nejsou povolené"}>
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button type="button" onClick={goNext}
                  className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-600 hover:bg-slate-100"
                  title="Další měsíc">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* dny v týdnu */}
            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {CS_DOW.map((d) => (
                <div key={d} className="text-[11px] font-bold text-slate-400 uppercase text-center py-1">{d}</div>
              ))}
            </div>
            {/* mřížka dní */}
            <div className="grid grid-cols-7 gap-0.5">
              {weeks.flat().map((d, i) => {
                if (d == null) return <div key={i} />;
                const dayIso = toIso(view.y, view.m0, d);
                const disabled = !!min && dayIso < min;
                const isSel = sel && dayIso === toIso(sel.y, sel.m0, sel.d);
                const isToday = dayIso === todayIso;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => pick(d)}
                    disabled={disabled}
                    className={`h-9 rounded-xl text-sm font-medium flex items-center justify-center transition-colors ${
                      disabled
                        ? "text-slate-300 bg-slate-50 cursor-not-allowed line-through"
                        : isSel
                          ? "bg-brand-deep text-white font-bold shadow-sm"
                          : isToday
                            ? "text-brand-deep ring-1 ring-brand-300 hover:bg-brand-50"
                            : "text-slate-700 hover:bg-brand-50"
                    }`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
